import imageCompression from "browser-image-compression";

// Phase B's upload flow: presign → PUT to R2 → finalize. Shared by
// TemplateAdmin (Phase D) and DocumentUploader (Phase I).
//
// The `target` arg picks the finalize endpoint:
//   - "document" finalizes via /api/uploads/:id/finalize → documents row
//   - "template" returns the pendingUploadId only; caller posts it to
//     /api/templates/<docType>/versions (which creates the template row)

type PresignArgs = {
  docType: string;
  propertyId?: number;
  entityId?: number;
  file: File;
  idempotencyKey?: string;
};

type PresignResponse = { pendingUploadId: number; url: string; expiresAt: string };

export async function presignAndPut(args: PresignArgs): Promise<{ pendingUploadId: number }> {
  let blob: Blob = args.file;
  let mimeType = args.file.type || "application/octet-stream";

  // Compress images client-side. PDFs and docx pass through.
  if (mimeType.startsWith("image/")) {
    blob = await imageCompression(args.file, {
      maxSizeMB: 1.2,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      initialQuality: 0.85,
    });
    mimeType = blob.type || mimeType;
  }

  const idempotencyKey = args.idempotencyKey ?? cryptoRandomKey();

  const presignRes = await fetch("/api/uploads/presign", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      docType: args.docType,
      propertyId: args.propertyId,
      entityId: args.entityId,
      mimeType,
      sizeBytes: blob.size,
      idempotencyKey,
    }),
  });
  if (!presignRes.ok) {
    const body = await presignRes.json().catch(() => ({}));
    throw new Error(body.message ?? `presign failed (${presignRes.status})`);
  }
  const { pendingUploadId, url } = (await presignRes.json()) as PresignResponse;

  const putRes = await fetch(url, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": mimeType },
  });
  if (!putRes.ok) {
    throw new Error(`upload failed (${putRes.status})`);
  }

  return { pendingUploadId };
}

export async function finalizeDocument(
  pendingUploadId: number,
  originalFilename?: string,
  displayName?: string,
  parentDocumentId?: number,
): Promise<{ documentId: number; status: string }> {
  const res = await fetch(`/api/uploads/${pendingUploadId}/finalize`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ originalFilename, displayName, parentDocumentId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `finalize failed (${res.status})`);
  }
  return res.json();
}

function cryptoRandomKey(): string {
  // 24-byte random hex; 48 chars satisfies the schema's min(8) max(128).
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
